import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../api/client';
import type { User, AuthResponse } from '../../types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/login', {
        email,
        password,
      });
      // If MFA is required, reject with the MFA data so LoginPage can handle it
      if (response.data.requiresMFA) {
        return rejectWithValue({ requiresMFA: true, mfaToken: response.data.mfaToken });
      }
      return response.data;
    } catch (err: any) {
      return rejectWithValue(extractApiError(err, 'Login failed'));
    }
  }
);

function extractApiError(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  // Backend may return { error, details: string[] } — surface details so users
  // know why they were rejected (e.g. password rules).
  const base = data.error || data.message || fallback;
  if (Array.isArray(data.details) && data.details.length > 0) {
    return `${base}: ${data.details.join('; ')}`;
  }
  return base;
}

export const register = createAsyncThunk(
  'auth/register',
  async (
    {
      email,
      password,
      displayName,
    }: {
      email: string;
      password: string;
      displayName?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/register', {
        email,
        password,
        displayName,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(extractApiError(err, 'Registration failed'));
    }
  }
);

export const getCurrentUser = createAsyncThunk('auth/me', async () => {
  const response = await apiClient.get<User>('/auth/me');
  return response.data;
});

export const logout = createAsyncThunk('auth/logout', async () => {
  await apiClient.post('/auth/logout');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(login.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      localStorage.setItem('accessToken', action.payload.accessToken);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
    });
    builder.addCase(login.rejected, (state, action) => {
      state.isLoading = false;
      // Login uses rejectWithValue for both MFA challenges (object payload)
      // and error strings — keep MFA path's payload object for the page to
      // detect, and surface human-readable string errors otherwise.
      const payload = action.payload as any;
      if (payload && typeof payload === 'object' && payload.requiresMFA) {
        // MFA path: don't show an error, the page reads the payload directly.
        state.error = null;
      } else if (typeof payload === 'string') {
        state.error = payload;
      } else {
        state.error = action.error.message || 'Login failed';
      }
    });

    // Register
    builder.addCase(register.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(register.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      localStorage.setItem('accessToken', action.payload.accessToken);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
    });
    builder.addCase(register.rejected, (state, action) => {
      state.isLoading = false;
      state.error =
        (action.payload as string) || action.error.message || 'Registration failed';
    });

    // Get current user
    builder.addCase(getCurrentUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getCurrentUser.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload;
    });
    builder.addCase(getCurrentUser.rejected, (state) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
    });

    // Logout
    builder.addCase(logout.fulfilled, (state) => {
      state.isAuthenticated = false;
      state.user = null;
    });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;